<template>
  <div class="wizard-step-depois">
    <div class="step-header">
      <h3>
        <i class="pi pi-camera"></i>
        {{ t('Depois do serviço') }}:
      </h3>
      <h4>
        {{
          t(
            'Nesta etapa você deve realizar os serviços e incluir uma foto do móvel DEPOIS do serviço concluído'
          )
        }}.
      </h4>
    </div>

    <!-- Progress bar -->
    <div class="progress-section">
      <p class="progress-text">
        {{ servicesWithAfterPhotos }} / {{ nonCanceledServices }}
        {{ t('serviços') }}
      </p>
      <ProgressBar :value="progressPercentage" :showValue="false" />
    </div>

    <!-- Services table -->
    <ServicesListView
      :services="activeServices"
      mode="depois"
      :show-edit-link="true"
      @edit-service="handleEditService"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import ServicesListView from './ServicesListView.vue';
import { useWizardServicos } from '../../../composables/useWizardServicos';
import { eventBus } from '../../../eventBus';
import { log } from '../../../utils/DebugLogger';

type Id = number | string;

interface CompromissoShape {
  compromisso_id?: number;
  compromisso_orcamento?: Id;
  servicos?: Array<{ compromisso_servico_status?: number; [key: string]: unknown }>;
  [key: string]: unknown;
}

interface Props {
  compromisso: CompromissoShape;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'next-step': [];
  'refresh': [];
}>();

const { t } = useI18n();
const { countNonCanceledServices, countServicesWithAfterPhotos } = useWizardServicos();

// Computed
const activeServices = computed(() => {
  if (!props.compromisso?.servicos) return [];
  return props.compromisso.servicos.filter((s) => s.compromisso_servico_status !== 6);
});

const nonCanceledServices = computed((): number => {
  return countNonCanceledServices(props.compromisso);
});

const servicesWithAfterPhotos = computed((): number => {
  return countServicesWithAfterPhotos(props.compromisso);
});

const progressPercentage = computed((): number => {
  if (nonCanceledServices.value === 0) return 0;
  return (servicesWithAfterPhotos.value / nonCanceledServices.value) * 100;
});

// Methods
const handleEditService = (servicoId: Id): void => {
  log('WizardStep2Depois', 'handleEditService', 'Opening edit modal for service', servicoId);
  eventBus.emit('compromisso-servico.open-edit', {
    compromissoServicoId: servicoId,
    compromissoId: props.compromisso.compromisso_id,
    orcamentoId: props.compromisso.compromisso_orcamento || 0
  });
};

const handlePhotoUploaded = (): void => {
  log('WizardStep2Depois', 'handlePhotoUploaded', 'Photo uploaded, refreshing data');
  emit('refresh');
};

// Lifecycle
onMounted(() => {
  log('WizardStep2Depois', 'mounted', 'Component mounted');
  eventBus.on('wizard-servicos.photo-uploaded', handlePhotoUploaded);
  eventBus.on('wizard-servicos.photo-deleted', handlePhotoUploaded);
});

onBeforeUnmount(() => {
  log('WizardStep2Depois', 'beforeUnmount', 'Cleaning up event listeners');
  eventBus.off('wizard-servicos.photo-uploaded', handlePhotoUploaded);
  eventBus.off('wizard-servicos.photo-deleted', handlePhotoUploaded);
});
</script>

<style scoped>
.wizard-step-depois {
  padding: 20px;
}

.step-header {
  margin-bottom: 30px;
}

.step-header h3 {
  font-size: 24px;
  margin-bottom: 10px;
}

.step-header h4 {
  font-size: 16px;
  color: #666;
  font-weight: normal;
}

.progress-section {
  margin-bottom: 20px;
}

.progress-text {
  font-size: 16px;
  margin-bottom: 10px;
  font-weight: 500;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .wizard-step-depois {
    padding: 10px;
  }

  .step-header h3 {
    font-size: 20px;
  }

  .step-header h4 {
    font-size: 14px;
  }
}
</style>
